/**
 * Constants and data definitions for Backbone detail view.
 */

import type { ConnectorType } from '../backbone.types';
import { mapConnectedServiceTypeToConnectorType } from '../requests/request';

/** @deprecated Prefer empty `vrfFilterSelection` (no filter). Kept for legacy string checks if needed. */
export const VRF_ALL = 'all';

/** True when no VRF filter is applied (show all). */
export function isVrfFilterUnfiltered(selection: readonly string[] | undefined): boolean {
	return selection == null || selection.length === 0;
}

/** OR match: row passes if its VRF display name is in `selection`, or if `selection` is empty. */
export function matchesVrfFilterSelection(
	vrfDisplayName: string | null | undefined,
	selection: readonly string[]
): boolean {
	if (selection.length === 0) return true;
	if (vrfDisplayName == null || vrfDisplayName === '') return false;
	return selection.includes(vrfDisplayName);
}

/**
 * UI groups for the connector-type filter. Aligned with bandwidth / topology naming:
 * - SDWAN: 'SDhub' + 'Sdwan'
 * - Firewall: 'Fwaas'
 * - Unknown API types → null (filtered out when any type filter is active).
 */
export type BackboneConnectorTypeGroup = 'SDWAN' | 'Cloud' | 'Port' | 'Firewall';

export const BACKBONE_CONNECTOR_TYPE_GROUPS: BackboneConnectorTypeGroup[] = [
	'SDWAN',
	'Cloud',
	'Port',
	'Firewall',
];

const BACKBONE_CONNECTOR_TYPE_GROUP_SET = new Set<string>(BACKBONE_CONNECTOR_TYPE_GROUPS);

/** Maps a UI {@link ConnectorType} to its filter group, or `null` when not represented in the UI filter. */
export function connectorTypeToGroup(
	type: ConnectorType | null | undefined
): BackboneConnectorTypeGroup | null {
	switch (type) {
		case 'Cloud':
			return 'Cloud';
		case 'Port':
			return 'Port';
		case 'SDhub':
		case 'Sdwan':
			return 'SDWAN';
		case 'Fwaas':
			return 'Firewall';
		default:
			return null;
	}
}

/** Resolve filter group from raw API `connectedServiceType` (BB-Connector list, routing rows). */
export function connectedServiceTypeToGroup(
	connectedServiceType: string | null | undefined
): BackboneConnectorTypeGroup | null {
	return connectorTypeToGroup(mapConnectedServiceTypeToConnectorType(connectedServiceType));
}

/** OR match for connector-type group selection. Empty selection = show all. */
export function matchesConnectorTypeGroupSelection(
	group: BackboneConnectorTypeGroup | null | undefined,
	selection: readonly string[]
): boolean {
	if (selection.length === 0) return true;
	if (group == null) return false;
	return selection.includes(group);
}

/** True when no connector-type filter is applied. */
export function isConnectorTypeFilterUnfiltered(
	selection: readonly string[] | undefined
): boolean {
	return selection == null || selection.length === 0;
}

/** Validate a string as one of the configured connector type groups. */
export function isBackboneConnectorTypeGroup(value: string): value is BackboneConnectorTypeGroup {
	return BACKBONE_CONNECTOR_TYPE_GROUP_SET.has(value);
}

/** BGP establishment filter option ids (global backbone filter, multi-OR within field). */
export type BackboneBgpStatusFilterId = 'up' | 'down';

export const BACKBONE_BGP_STATUS_FILTER_UP: BackboneBgpStatusFilterId = 'up';
export const BACKBONE_BGP_STATUS_FILTER_DOWN: BackboneBgpStatusFilterId = 'down';

export function isBgpStatusFilterUnfiltered(selection: readonly string[] | undefined): boolean {
	return selection == null || selection.length === 0;
}

/** BB-Connector row: `isEstablished === true` → up; otherwise down. */
export function connectorIsEstablishedToBgpStatusFilterId(
	isEstablished: boolean | undefined
): BackboneBgpStatusFilterId {
	return isEstablished === true ? BACKBONE_BGP_STATUS_FILTER_UP : BACKBONE_BGP_STATUS_FILTER_DOWN;
}

/** BGP session row mapped to {@link Connector}: Established → up, else down. */
export function bgpSessionRowToBgpStatusFilterId(
	bgp: 'Established' | 'Active'
): BackboneBgpStatusFilterId {
	return bgp === 'Established' ? BACKBONE_BGP_STATUS_FILTER_UP : BACKBONE_BGP_STATUS_FILTER_DOWN;
}

/**
 * OR within field. Empty selection = show all.
 * When `rowFilterId` is null (e.g. dimension not on row), the row is not restricted by this filter.
 */
export function matchesBgpStatusFilterSelection(
	rowFilterId: BackboneBgpStatusFilterId | null,
	selection: readonly string[]
): boolean {
	if (selection.length === 0) return true;
	if (rowFilterId == null) return true;
	return selection.includes(rowFilterId);
}

export const BACKBONE_DETAIL_TABS = [
    { label: 'Connectors Overview', value: 'connectors' },
    { label: 'Topology', value: 'topology' },
    { label: 'BGP Sessions', value: 'bgp' },
    { label: 'Routing Table', value: 'routing' },
    { label: 'Policy Rules · Beta', value: 'policy' },
];
